const MAX_HEAD_ROLL = 20;
const MAX_HEAD_PITCH = 20;
const MAX_HEAD_YAW = 40;

const BLUR_THRESHOLD = 20;

// The roll, pitch, and yaw returned by the face API are used to find head direction
// These angles are hard to explain in texts, easier to understand from pictures
// But here are the definitions from an aviation website:
// Rotation around the front-to-back axis is called roll.
// Rotation around the side-to-side axis is called pitch.
// Rotation around the vertical axis is called yaw.
const isHeadStraight = (angle: { roll: number | undefined; pitch: number | undefined; yaw: number | undefined }) => {
  const { roll, pitch, yaw } = angle;
  if (roll === undefined || pitch === undefined || yaw === undefined) {
    return false;
  }
  if (Math.abs(roll) > MAX_HEAD_ROLL) {
    return false;
  }
  if (Math.abs(pitch) > MAX_HEAD_PITCH) {
    return false;
  }
  if (Math.abs(yaw) > MAX_HEAD_YAW) {
    return false;
  }
  return true;
};

// If the face is too small, we consider it's far (normal optics) and vice versa
const isFaceCloseEnough = (dimensions: {
  frameWidth: number;
  frameHeight: number;
  faceWidth: number;
  faceHeight: number;
}) => {
  const { frameWidth, frameHeight, faceWidth, faceHeight } = dimensions;
  if (!frameWidth || !frameHeight || !faceWidth || !faceHeight) {
    return false;
  }
  if (faceWidth < 0.5 * frameWidth && faceHeight < 0.5 * frameHeight) {
    return false;
  }
  return true;
};

const isFaceTooClose = (dimensions: {
  frameWidth: number;
  frameHeight: number;
  faceWidth: number;
  faceHeight: number;
}) => {
  const { frameWidth, frameHeight, faceWidth, faceHeight } = dimensions;
  if (!frameWidth || !frameHeight || !faceWidth || !faceHeight) {
    return false;
  }
  // Face fills more than 110% of the frame in either dimension → too close
  return faceWidth > frameWidth * 1.1 || faceHeight > frameHeight * 1.1;
};

// Nothing fancy
// And we find the left, right, top, bottom given the width and height of the frame and the video
// Face API already gives us the face location and dimensions
const isFaceInTheFrame = (dimensions: {
  videoWidth: number;
  videoHeight: number;
  frameWidth: number;
  frameHeight: number;
  faceWidth: number;
  faceHeight: number;
  faceX: number;
  faceY: number;
  frameOffsetX: number;
  frameOffsetY: number;
}) => {
  const {
    videoWidth,
    videoHeight,
    frameWidth,
    frameHeight,
    faceWidth,
    faceHeight,
    faceX,
    faceY,
    frameOffsetX,
    frameOffsetY,
  } = dimensions;

  const faceLeft = faceX;
  const faceRight = faceX + faceWidth;
  const faceTop = faceY;
  const faceBottom = faceY + faceHeight;

  const frameLeft = Math.abs(videoWidth - frameWidth) / 2 + frameOffsetX;
  const frameRight = frameLeft + frameWidth;
  const frameTop = Math.abs(videoHeight - frameHeight) / 2 + frameOffsetY;
  const frameBottom = frameTop + frameHeight;

  const leftOffset = faceLeft - frameLeft;
  const rightOffset = frameRight - faceRight;
  const topOffset = faceTop - frameTop;
  const bottomOffset = frameBottom - faceBottom;

  // 5% error margin
  if (leftOffset < 0 && Math.abs((leftOffset * 100) / faceWidth) > 5) {
    return false;
  }
  if (rightOffset < 0 && Math.abs((rightOffset * 100) / faceWidth) > 5) {
    return false;
  }
  if (topOffset < 0 && Math.abs((topOffset * 100) / faceHeight) > 5) {
    return false;
  }
  if (bottomOffset < 0 && Math.abs((bottomOffset * 100) / faceHeight) > 5) {
    return false;
  }

  return true;
};

const calculateFaceAngleFromMatrix = (matrix: ArrayLike<number>): { roll: number; pitch: number; yaw: number } => {
  const r = (row: number, col: number) => matrix[col * 4 + row];

  const pitch = Math.asin(-r(2, 0));
  const yaw = Math.atan2(r(1, 0), r(0, 0));
  const roll = Math.atan2(r(2, 1), r(2, 2));

  const toDeg = (rad: number) => (rad * 180) / Math.PI;
  return { pitch: toDeg(pitch), yaw: toDeg(yaw), roll: toDeg(roll) };
};

const isFaceBlurry = (
  canvas: HTMLCanvasElement,
  box: { x: number; y: number; width: number; height: number },
): boolean => {
  const w = Math.max(1, Math.floor(box.width));
  const h = Math.max(1, Math.floor(box.height));

  const cropCanvas = document.createElement('canvas');
  cropCanvas.width = w;
  cropCanvas.height = h;
  const ctx = cropCanvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return false;
  ctx.drawImage(canvas, box.x, box.y, box.width, box.height, 0, 0, w, h);

  const imgData = ctx.getImageData(0, 0, w, h);
  const pixels = imgData.data;

  // Grayscale conversion
  const gray = new Float32Array(w * h);
  for (let i = 0; i < gray.length; i++) {
    const offset = i * 4;
    gray[i] = 0.299 * pixels[offset] + 0.587 * pixels[offset + 1] + 0.114 * pixels[offset + 2];
  }

  // Laplacian kernel: [0,-1,0, -1,4,-1, 0,-1,0]
  let sum = 0;
  let sumSq = 0;
  const n = (w - 2) * (h - 2);
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const val =
        -gray[(y - 1) * w + x] -
        gray[y * w + (x - 1)] +
        4 * gray[y * w + x] -
        gray[y * w + (x + 1)] -
        gray[(y + 1) * w + x];
      sum += val;
      sumSq += val * val;
    }
  }

  if (n === 0) return false;
  const mean = sum / n;
  const variance = sumSq / n - mean * mean;

  return variance < BLUR_THRESHOLD;
};

type Pt = { x: number; y: number };

// Sunglasses detection: compare eye-region luminance against skin reference
// (forehead + nose + cheeks). Only opaque dark lenses are detected — tinted/
// reflective lenses require a dedicated ML classifier.
const SUNGLASSES_LUMINANCE_RATIO = 0.65;

const sampleLuminance = (ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number): number => {
  const x = Math.max(0, Math.floor(cx - r));
  const y = Math.max(0, Math.floor(cy - r));
  const size = Math.max(1, Math.floor(r * 2));
  const { data } = ctx.getImageData(x, y, size, size);
  let sum = 0;
  for (let i = 0; i < data.length; i += 4) {
    sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  }
  return sum / (data.length / 4);
};

const isWearingSunglasses = (canvas: HTMLCanvasElement, landmarks: Pt[]): boolean => {
  if (landmarks.length < 468) return false;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return false;

  const w = canvas.width;
  const h = canvas.height;

  // Sampling radius: ~18% of inter-eye distance to cover the full lens area
  const eyeRadius = Math.abs(landmarks[33].x - landmarks[263].x) * w * 0.18;
  if (eyeRadius < 2) return false;

  const rEyeCx = ((landmarks[33].x + landmarks[133].x) / 2) * w;
  const rEyeCy = ((landmarks[159].y + landmarks[145].y) / 2) * h;
  const lEyeCx = ((landmarks[263].x + landmarks[362].x) / 2) * w;
  const lEyeCy = ((landmarks[386].y + landmarks[374].y) / 2) * h;

  const eyeLum =
    (sampleLuminance(ctx, rEyeCx, rEyeCy, eyeRadius) + sampleLuminance(ctx, lEyeCx, lEyeCy, eyeRadius)) / 2;

  // forehead (10) + nose tip (4) + cheeks (234, 454) as skin-tone reference
  const refLum =
    (sampleLuminance(ctx, landmarks[10].x * w, landmarks[10].y * h, eyeRadius) +
      sampleLuminance(ctx, landmarks[4].x * w, landmarks[4].y * h, eyeRadius) +
      sampleLuminance(ctx, landmarks[234].x * w, landmarks[234].y * h, eyeRadius) +
      sampleLuminance(ctx, landmarks[454].x * w, landmarks[454].y * h, eyeRadius)) /
    4;

  if (refLum === 0) return false;
  return eyeLum / refLum < SUNGLASSES_LUMINANCE_RATIO;
};

// Kept for backward compatibility — not used in active detection path (head angle
// is now derived from the MediaPipe transformation matrix via calculateFaceAngleFromMatrix).
const calculateFaceAngle = (
  mesh: { positions: Array<{ x: number; y: number }> } | null | undefined,
): { roll: number | undefined; pitch: number | undefined; yaw: number | undefined } => {
  const angle: { roll: number | undefined; pitch: number | undefined; yaw: number | undefined } = {
    roll: undefined,
    pitch: undefined,
    yaw: undefined,
  };

  if (!mesh || mesh.positions?.length !== 68) {
    return angle;
  }

  const degrees = (radians: number) => (radians * 180) / Math.PI;
  const calcLengthBetweenTwoPoints = (a: { x: number; y: number }, b: { x: number; y: number }) =>
    Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
  const pt = mesh.positions;

  const calcRoll = (lever: { x: number; y: number }, pivot: { x: number; y: number }) => {
    const hypotenuse = Math.hypot(pivot.x - lever.x, pivot.y - lever.y);
    const opposite = pivot.y - lever.y;
    const angleInDegrees = degrees(Math.asin(opposite / hypotenuse));
    const normalizeAngle = Math.floor(90 - angleInDegrees);
    const tiltDirection = pivot.x - lever.x < 0 ? -1 : 1;
    return normalizeAngle * tiltDirection;
  };

  const calcPitch = (
    leftPoint: { x: number; y: number },
    midPoint: { x: number; y: number },
    rightPoint: { x: number; y: number },
  ) => {
    const base = calcLengthBetweenTwoPoints(leftPoint, rightPoint);
    const baseCoords = { x: (leftPoint.x + rightPoint.x) / 2, y: (leftPoint.y + rightPoint.y) / 2 };
    const midToBaseLength = calcLengthBetweenTwoPoints(midPoint, baseCoords);
    const angleInDegrees = Math.floor(degrees(Math.atan(midToBaseLength / base)));
    const direction = baseCoords.y - midPoint.y < 0 ? -1 : 1;
    return angleInDegrees * direction;
  };

  const calcYaw = (
    leftPoint: { x: number; y: number },
    midPoint: { x: number; y: number },
    rightPoint: { x: number; y: number },
  ) => {
    const leftToMidpoint = Math.floor(leftPoint.x - midPoint.x);
    const rightToMidpoint = Math.floor(midPoint.x - rightPoint.x);
    return leftToMidpoint - rightToMidpoint;
  };

  angle.roll = calcRoll(pt[27], pt[66]);
  angle.pitch = calcPitch(pt[14], pt[30], pt[2]);
  angle.yaw = calcYaw(pt[14], pt[33], pt[2]);
  return angle;
};

export {
  calculateFaceAngle,
  calculateFaceAngleFromMatrix,
  isFaceBlurry,
  isFaceCloseEnough,
  isFaceInTheFrame,
  isFaceTooClose,
  isHeadStraight,
  isWearingSunglasses,
};
